import { Button, FormControlLabel, MenuItem, Radio, RadioGroup, TextField } from '@mui/material';
import { FieldArray, Form, Formik } from 'formik';
import { ModalAllder } from 'layouts/ModalAllder';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { customerHandler, masterHandler } from '../../../redux/handlers';

export function ChangePackageModal({ modal, setModal, onSuccess }) {
  const dispatch = useDispatch();
  const { businessID } = useParams();
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [packageData, setPackageData] = useState({
    packages: [],
    planRenewals: [],
    changeResults: [],
    expirations: [],
    permissions: []
  });

  const resetState = () => {
    setSelectedBusiness(null);
    setPackageData({
      packages: [],
      planRenewals: [],
      changeResults: [],
      expirations: [],
      permissions: []
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!businessID) return;

      try {
        const businessData = await dispatch(customerHandler.getBusinessGroupById(businessID));
        const [planRenewalsData, changeResultsData, expirationsData, packagesData, permissionsData] = await Promise.all(
          [
            dispatch(masterHandler.getPackagePlanRenewals()),
            dispatch(masterHandler.getPackageChangeResults()),
            dispatch(masterHandler.getPackageExpirations()),
            dispatch(customerHandler.getPackageList()),
            dispatch(customerHandler.getPermissionsAdditionalByPackageMainId(businessData?.business?.package_main_id))
          ]
        );

        setSelectedBusiness(businessData?.business || null);
        setPackageData({
          packages: packagesData || [],
          planRenewals: planRenewalsData || [],
          changeResults: changeResultsData || [],
          expirations: expirationsData || [],
          permissions: permissionsData || []
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (modal.open) {
      resetState();
      fetchData();
    }
  }, [dispatch, modal.open, businessID]);

  const initialValues = {
    current_package: selectedBusiness?.packageMain?.name || '',
    current_renewal_cycle: selectedBusiness?.packagePlanRenewal?.name || '',
    package: selectedBusiness?.package_main_id || '',
    renewal_cycle: selectedBusiness?.package_plan_renewal_id || '',
    package_additional:
      selectedBusiness?.businessPackageAdditional?.map(item => ({
        id: item.package_additional_permission_id,
        qty: item.qty
      })) || [],
    change_result: selectedBusiness?.package_change_result_id || packageData.changeResults[0]?.id || '',
    expiration_type: selectedBusiness?.package_expiration_id || packageData.expirations[0]?.id || ''
  };

  const validationSchema = Yup.object({
    package: Yup.string().required('กรุณาเลือกแพ็กเกจ'),
    renewal_cycle: Yup.string().required('กรุณาเลือกรอบการต่ออายุ'),
    change_result: Yup.string().required('กรุณาเลือกผลการเปลี่ยนแพ็กเกจ'),
    expiration_type: Yup.string().required('กรุณาเลือกรูปแบบการหมดอายุ'),
    package_additional: Yup.array()
      .of(
        Yup.object({
          id: Yup.string().required('กรุณาเลือกแพ็กเกจเสริม'),
          qty: Yup.number()
            .required('กรุณาระบุจำนวน')
            .positive('จำนวนต้องมากกว่า 0')
            .integer('จำนวนต้องเป็นจำนวนเต็ม')
            .min(1, 'จำนวนต้องอย่างน้อย 1')
        })
      )
      .test('unique-packages', 'มีการเลือกแพ็กเกจเสริมซ้ำ', function (value) {
        if (!value || value.length === 0) return true;

        const validItems = value.filter(item => item.id && item.id !== '');

        const ids = validItems.map(item => item.id);
        const uniqueIds = new Set(ids);

        if (ids.length !== uniqueIds.size) {
          const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
          const duplicateIndex = ids.lastIndexOf(duplicateIds[0]);

          return this.createError({
            path: `package_additional[${duplicateIndex}].id`,
            message: 'แพ็กเกจเสริมนี้ถูกเลือกแล้ว'
          });
        }

        return true;
      })
  });

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    if (!selectedBusiness) return;

    try {
      let changeDate = null;

      const selectedChangeResult = packageData.changeResults.find(result => result.id === Number(values.change_result));

      if (selectedChangeResult && selectedChangeResult.code === 'NEXT') {
        const now = new Date();
        const nextMonthDate = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
        changeDate = nextMonthDate.toISOString();
      }
      const payload = {
        businessId: selectedBusiness.id,
        packageMainId: values.package,
        packageChangeResultId: Number(values.change_result),
        packageExpirationId: Number(values.expiration_type),
        packagePlanRenewalId: values.renewal_cycle,
        changeDate: changeDate,
        packageAdditionalPermissions: values.package_additional
          .filter(item => item.id && item.qty)
          .map(item => ({
            packageAdditionalPermissionId: item.id,
            qty: parseInt(item.qty)
          }))
      };

      const response = await dispatch(customerHandler.updateBusinessPackage(selectedBusiness.id, payload));

      if (response) {
        if (onSuccess) {
          await onSuccess();
        }
        setModal(prev => ({ ...prev, open: false }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const getFieldError = (errors, touched, fieldPath) => {
    const pathArray = fieldPath.split('.');
    let error = errors;
    let touch = touched;

    for (const key of pathArray) {
      if (error && error[key] !== undefined) {
        error = error[key];
      } else {
        error = undefined;
        break;
      }

      if (touch && touch[key] !== undefined) {
        touch = touch[key];
      } else {
        touch = undefined;
        break;
      }
    }

    return touch && error ? error : '';
  };

  const getAvailablePackages = (currentIndex, values) => {
    const selectedIds = values.package_additional
      .map((item, index) => (index !== currentIndex ? item.id : null))
      .filter(id => id && id !== '');

    return packageData.permissions.filter(permission => !selectedIds.includes(permission.id.toString()));
  };

  if (!selectedBusiness) {
    return null;
  }

  return (
    <ModalAllder modal={modal} setModal={setModal} htitle={'เปลี่ยนแพ็คเกจ'} hbtn={true}>
      <div className="py-1">
        <div className="">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ handleSubmit, handleChange, touched, errors, values, setFieldValue, isSubmitting }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <div className="text-[#4C4E64] font-semibold mb-4">แพ็กเกจปัจจุบัน</div>
                <div className="grid grid-cols-12 gap-4 mb-4">
                  <div className="col-span-6">
                    <TextField
                      label="เลือกแพ็กเกจ"
                      variant="outlined"
                      InputProps={{ sx: { borderRadius: '7px' } }}
                      fullWidth
                      disabled
                      sx={{
                        '.Mui-disabled': {
                          background: '#F8F8F9'
                        }
                      }}
                      name="current_package"
                      value={values.current_package}
                    />
                  </div>
                  <div className="col-span-6">
                    <TextField
                      label="รอบการต่ออายุ"
                      variant="outlined"
                      InputProps={{ sx: { borderRadius: '7px' } }}
                      fullWidth
                      disabled
                      sx={{
                        '.Mui-disabled': {
                          background: '#F8F8F9'
                        }
                      }}
                      name="current_renewal_cycle"
                      value={values.current_renewal_cycle}
                    />
                  </div>
                </div>

                <div className="text-[#4C4E64] font-semibold mb-4">แพ็กเกจหลัก</div>
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-6">
                    <TextField
                      select
                      label="เลือกแพ็กเกจ"
                      InputProps={{ sx: { borderRadius: '7px' } }}
                      fullWidth
                      onChange={handleChange}
                      name="package"
                      error={!!(touched.package && errors.package)}
                      helperText={touched.package && errors.package}
                      value={values.package}
                    >
                      {packageData.packages.map(pkg => (
                        <MenuItem key={pkg.id} value={pkg.id}>
                          {pkg.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                  <div className="col-span-6">
                    <TextField
                      select
                      label="รอบการต่ออายุ"
                      InputProps={{ sx: { borderRadius: '7px' } }}
                      fullWidth
                      onChange={handleChange}
                      name="renewal_cycle"
                      error={!!(touched.renewal_cycle && errors.renewal_cycle)}
                      helperText={touched.renewal_cycle && errors.renewal_cycle}
                      value={values.renewal_cycle}
                    >
                      {packageData.planRenewals.map(renewal => (
                        <MenuItem key={renewal.id} value={renewal.id}>
                          {renewal.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                </div>

                <div className="mt-4">
                  {values.package_additional?.length > 0 && (
                    <div className="text-[#4C4E64] font-semibold mb-4">แพ็กเกจเสริม</div>
                  )}
                  <FieldArray
                    name="package_additional"
                    render={arrayHelpers => (
                      <>
                        {values.package_additional?.map((item, index) => {
                          const typeError = getFieldError(errors, touched, `package_additional.${index}.id`);
                          const qtyError = getFieldError(errors, touched, `package_additional.${index}.qty`);
                          const availablePackages = getAvailablePackages(index, values);

                          return (
                            <div key={index} className="grid grid-cols-12 gap-4 mb-4">
                              <div className="col-span-6">
                                <TextField
                                  select
                                  label="เลือกแพ็กเกจ"
                                  InputProps={{ sx: { borderRadius: '7px' } }}
                                  fullWidth
                                  value={values.package_additional[index].id}
                                  error={!!typeError}
                                  helperText={typeError}
                                  onChange={e => {
                                    setFieldValue(`package_additional.${index}.id`, e.target.value);
                                  }}
                                >
                                  {/* แสดงตัวเลือกปัจจุบัน (ถ้ามี) แม้ว่าจะถูกเลือกแล้วก็ตาม */}
                                  {values.package_additional[index].id &&
                                    packageData.permissions
                                      .filter(
                                        permission => permission.id.toString() === values.package_additional[index].id
                                      )
                                      .map(permission => (
                                        <MenuItem key={permission.id} value={permission.id}>
                                          {permission.permissionName}
                                        </MenuItem>
                                      ))}
                                  {/* แสดงตัวเลือกที่ยังไม่ถูกเลือก */}
                                  {availablePackages.map(permission => (
                                    <MenuItem key={permission.id} value={permission.id}>
                                      {permission.permissionName}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </div>
                              <div className="col-span-5">
                                <TextField
                                  label="จำนวน"
                                  variant="outlined"
                                  InputProps={{ sx: { borderRadius: '7px' } }}
                                  fullWidth
                                  type="number"
                                  inputProps={{ min: 1 }}
                                  value={values.package_additional[index].qty}
                                  error={!!qtyError}
                                  helperText={qtyError}
                                  onChange={e => {
                                    const value = e.target.value;

                                    if (value === '' || (!isNaN(value) && parseInt(value) >= 0)) {
                                      setFieldValue(`package_additional.${index}.qty`, value);
                                    }
                                  }}
                                />
                              </div>
                              <div className="col-span-1 flex items-center justify-center">
                                <button
                                  type="button"
                                  onClick={() => arrayHelpers.remove(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        <div className="my-3">
                          <Button
                            variant="text"
                            className="w-[180px] !text-[#2DC76D] rounded-[8px] !text-base h-[44px] mr-3"
                            onClick={() =>
                              arrayHelpers.push({
                                id: '',
                                qty: ''
                              })
                            }
                          >
                            + เพิ่มแพ็กเกจเสริม
                          </Button>
                        </div>
                      </>
                    )}
                  />
                </div>

                <div className="text-[#4C4E64] font-semibold mb-3 mt-4">ผลการเปลี่ยนแพ็กเกจ</div>
                <RadioGroup name="change_result" value={values.change_result} onChange={handleChange}>
                  <div className="grid grid-cols-12 gap-4">
                    {packageData.changeResults.map(result => (
                      <div key={result.id} className="col-span-6">
                        <FormControlLabel value={result.id} control={<Radio />} label={result.name} />
                      </div>
                    ))}
                  </div>
                </RadioGroup>
                {touched.change_result && errors.change_result && (
                  <div className="text-red-500 text-sm mt-1 ml-2">{errors.change_result}</div>
                )}

                <div className="text-[#4C4E64] font-semibold mb-3 mt-3">รูปแบบการหมดอายุ</div>
                <RadioGroup name="expiration_type" value={values.expiration_type} onChange={handleChange}>
                  <div className="grid grid-cols-12 gap-4">
                    {packageData.expirations.map(expiration => (
                      <div key={expiration.id} className="col-span-6">
                        <FormControlLabel value={expiration.id} control={<Radio />} label={expiration.name} />
                        <div className="text-sm text-[#8F8C8C] ml-[30px]">
                          {expiration.id === 'AUTO'
                            ? 'ก่อนหวันหมดอายุให้ระบบต่ออายุอัตโนมัติ'
                            : 'เมื่อหมดอายุระบบจะไม่ต่ออายุอัตโนมัติ'}
                        </div>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
                {touched.expiration_type && errors.expiration_type && (
                  <div className="text-red-500 text-sm mt-1 ml-2">{errors.expiration_type}</div>
                )}

                <div className="text-center pt-5 pb-4 btn-group-control">
                  <Button
                    type="button"
                    className="btn-st-5 me-3 !border-[#6D788D] !rounded-lg !text-[#6D788D]"
                    variant="outlined"
                    size="large"
                    disabled={isSubmitting}
                    onClick={() => setModal(prev => ({ ...prev, open: false }))}
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    type="submit"
                    className="btn-st-3 !bg-[#409FFB] !rounded-lg !text-white"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                  >
                    บันทึก
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </ModalAllder>
  );
}
